import React, { useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LabelList,
} from "recharts";
import { getOrBuildCache } from "../../utils/chart/financial-cache.utils";
import { getQuarterlyPeriodData, resolveProjectEndChartMarker } from "../../utils/chart/chart-period.utils";
import ProjectEndReferenceLine from "./ProjectEndReferenceLine";
import type { Revenue, Expense } from "../../types/startup.types";
import { CHART_COLORS } from "@/constants/chart-colors";

const QUARTERLY_COLORS = {
  revenue: CHART_COLORS.revenue,
  expenses: CHART_COLORS.expense,
} as const;

interface EventData {
  id: string;
  date: Date;
  type: "funding" | "milestone" | "launch" | "asset" | "custom";
  priority: "high" | "medium" | "low";
  description: string;
  amount?: number;
  icon?: string;
  color?: string;
  monthlyAppreciationRate?: number;
  minimumValue?: number;
}

interface ChartData {
  startDate: Date;
  endDate: Date;
  projectEndDate?: Date;
  initialBalance: number;
  expenses: Expense[];
  revenues?: Revenue[];
  events: EventData[];
}

interface QuarterlyCashFlowChartProps {
  data: ChartData;
  visibleDateRange: {
    startDate: Date;
    endDate: Date;
  };
  height?: number;
}

function formatCompactCurrency(value: number): string {
  const absoluteValue = Math.abs(Math.round(value));
  if (absoluteValue >= 1_000_000) {
    return `$${(absoluteValue / 1_000_000).toFixed(1)}M`;
  }
  if (absoluteValue >= 1_000) {
    return `$${(absoluteValue / 1_000).toFixed(0)}K`;
  }
  return `$${absoluteValue.toLocaleString()}`;
}

function formatSignedCurrency(value: number): string {
  const rounded = Math.round(value);
  const prefix = rounded > 0 ? "+" : rounded < 0 ? "-" : "";
  return `${prefix}${formatCompactCurrency(rounded)}`;
}

function formatBarLabel(value: React.ReactNode): string {
  return formatCompactCurrency(typeof value === "number" ? value : 0);
}

interface QuarterTickProps {
  x?: number;
  y?: number;
  payload?: { value: string; index: number };
  chartData: Array<{ label: string; netOperationalFlow: number }>;
}

function QuarterAxisTick({ x = 0, y = 0, payload, chartData }: QuarterTickProps) {
  if (!payload) return null;

  const point = chartData[payload.index];
  const net = point?.netOperationalFlow ?? 0;
  const netColor = net >= 0 ? QUARTERLY_COLORS.revenue : QUARTERLY_COLORS.expenses;

  return (
    <g transform={`translate(${x},${y})`}>
      <text
        y={0}
        dy={12}
        textAnchor="middle"
        fill="rgba(255,255,255,0.55)"
        fontSize={11}
      >
        {payload.value}
      </text>
      <text
        y={0}
        dy={26}
        textAnchor="middle"
        fill={netColor}
        fontSize={10}
        fontWeight={600}
      >
        {formatSignedCurrency(net)}
      </text>
    </g>
  );
}

const QuarterlyCashFlowChart: React.FC<QuarterlyCashFlowChartProps> = ({
  data,
  visibleDateRange,
  height = 360,
}) => {
  const { chartData, projectEndMarker } = useMemo(() => {
    const cache = getOrBuildCache(data);
    const quarters = getQuarterlyPeriodData(
      cache,
      visibleDateRange.startDate,
      visibleDateRange.endDate,
    );

    const projectEndMarker = resolveProjectEndChartMarker(
      data.projectEndDate,
      visibleDateRange.startDate,
      visibleDateRange.endDate,
      quarters.map((quarter) => ({
        label: quarter.label,
        periodStart: quarter.periodStart,
        periodEnd: quarter.periodEnd,
      })),
    );

    return { chartData: quarters, projectEndMarker };
  }, [data, visibleDateRange]);

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;

    const point = payload[0].payload;
    return (
      <div className="rounded-lg bg-surface-elevated px-3 py-2 text-xs text-white shadow-glow">
        <p className="mb-2 font-semibold">{label}</p>
        <div className="space-y-1">
          <p className="text-brand">
            Revenue: ${Math.round(point.revenue).toLocaleString()}
          </p>
          <p className="text-expense">
            Costs: ${Math.round(point.expenses).toLocaleString()}
          </p>
          <p
            className={
              point.netOperationalFlow >= 0 ? "text-brand" : "text-expense"
            }
          >
            Net: {formatSignedCurrency(point.netOperationalFlow)}
          </p>
        </div>
      </div>
    );
  };

  if (chartData.length === 0) {
    return (
      <div className="sh-panel overflow-hidden">
        <div className="border-b border-white/10 bg-surface-secondary/60 px-4 py-2">
          <h4 className="text-sm font-medium text-white/75">Quarterly Cash Flow</h4>
        </div>
        <div className="p-4 text-sm text-white/45">No quarterly data in this range.</div>
      </div>
    );
  }

  return (
    <div className="sh-panel overflow-hidden">
      <div className="border-b border-white/10 bg-surface-secondary/60 px-4 py-2">
        <h4 className="text-sm font-medium text-white/75">
          Quarterly Cash Flow ({chartData.length}{" "}
          {chartData.length === 1 ? "quarter" : "quarters"})
        </h4>
        <div className="mt-1 flex items-center gap-4 text-xs text-white/45">
          <div className="flex items-center gap-1">
            <div
              className="h-3 w-3 rounded"
              style={{ backgroundColor: QUARTERLY_COLORS.revenue }}
            />
            <span>Revenue</span>
          </div>
          <div className="flex items-center gap-1">
            <div
              className="h-3 w-3 rounded"
              style={{ backgroundColor: QUARTERLY_COLORS.expenses }}
            />
            <span>Costs</span>
          </div>
          <span>Net shown under each quarter</span>
        </div>
      </div>
      <div className="p-4">
        <ResponsiveContainer width="100%" height={height}>
          <BarChart
            data={chartData}
            margin={{ top: 24, right: 16, left: 8, bottom: 36 }}
            barCategoryGap="24%"
            barGap={4}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
            <XAxis
              dataKey="label"
              tick={(props) => (
                <QuarterAxisTick {...props} chartData={chartData} />
              )}
              axisLine={false}
              tickLine={false}
              interval={0}
              height={48}
            />
            <YAxis
              tick={{ fontSize: 11, fill: "rgba(255,255,255,0.5)" }}
              axisLine={false}
              tickLine={false}
              tickFormatter={formatCompactCurrency}
            />
            <Tooltip content={<CustomTooltip />} />
            {projectEndMarker ? (
              <ProjectEndReferenceLine periodLabel={projectEndMarker.periodLabel} />
            ) : null}
            <Bar
              dataKey="revenue"
              name="Revenue"
              fill={QUARTERLY_COLORS.revenue}
              radius={[4, 4, 0, 0]}
              maxBarSize={42}
            >
              <LabelList
                dataKey="revenue"
                position="top"
                formatter={formatBarLabel}
                fill={QUARTERLY_COLORS.revenue}
                fontSize={10}
              />
            </Bar>
            <Bar
              dataKey="expenses"
              name="Costs"
              fill={QUARTERLY_COLORS.expenses}
              radius={[4, 4, 0, 0]}
              maxBarSize={42}
            >
              <LabelList
                dataKey="expenses"
                position="top"
                formatter={formatBarLabel}
                fill={QUARTERLY_COLORS.expenses}
                fontSize={10}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default QuarterlyCashFlowChart;
